import { useState, useCallback } from "react"
import { lumi } from "../lib/lumi"

export type SpecStatus = "draft" | "confirmed"
export type RevisionChangeType = "create" | "update" | "delete"
export type ApprovalStatus = "pending" | "approved" | "rejected"

// [정책] 개정안 이름을 이 값("최초 작성")으로 승인하면 최초 작성으로 간주하여
// 총 수정 반영 횟수(totalRevisionCount)를 증가시키지 않는다. (최초 작성은 "수정"이 아님)
export const INITIAL_WRITE_NAME = "최초 작성"

// 제작에 사용된 부품(형번/수량) 구조화 항목
export interface SpecPart {
  partName?: string
  partNumber?: string // 형번(품번)
  quantity?: number // 수량
  maker?: string // 제조사/브랜드
  remark?: string // 비고
}

// parts 배열 <-> JSON 문자열 직렬화 헬퍼 (revision before/after 저장용)
export function serializeParts(parts?: SpecPart[]): string {
  if (!parts || parts.length === 0) return ""
  try {
    return JSON.stringify(parts)
  } catch {
    return ""
  }
}

export function parseParts(raw?: string): SpecPart[] {
  if (!raw) return []
  try {
    const v = JSON.parse(raw)
    return Array.isArray(v) ? (v as SpecPart[]) : []
  } catch {
    return []
  }
}

export interface EquipmentSpec {
  _id: string
  specTitle: string
  sourceCompany?: string
  docNumber?: string
  equipmentId?: string
  currentVersion: number
  status: SpecStatus
  archived?: boolean // 보관(Archive) 여부. true면 숨김 처리
  referencedUsers?: { userId: string; userName: string; teamName?: string; email?: string }[]
  lastApprovedAt?: string // 마지막 사양 승인(개정묶음 승인) 일시 = 마지막 수정일
  lastApprovedVersion?: number
  lastChangesetName?: string // 마지막으로 승인된 개정안 이름 (현재 버전 옆 표시용)
  totalRevisionCount?: number // 실제로 승인 반영된 누적 수정 건수
  creator: string
  creatorName: string
  createdAt: string
  updatedAt: string
}

export interface SpecItem {
  _id: string
  specId: string
  category: string
  subCategory: string
  requirement?: string
  guideline?: string
  actualSpec?: string // 실제 제작 사양 (승인 불필요, 모든 사용자 직접 수정)
  parts?: SpecPart[] // 제작 부품 목록(형번/수량)
  unit?: string
  remark?: string
  sortOrder?: number
  version?: number
  creator: string
  createdAt: string
  updatedAt: string
}

export interface SpecItemRevision {
  _id: string
  specId: string
  itemId?: string
  changeType: RevisionChangeType
  category: string
  subCategory: string
  requirementBefore?: string
  requirementAfter?: string
  guidelineBefore?: string
  guidelineAfter?: string
  remarkBefore?: string // 비고 변경 전
  remarkAfter?: string // 비고 변경 후
  partsBefore?: string // 부품 목록 변경 전 (JSON 문자열)
  partsAfter?: string // 부품 목록 변경 후 (JSON 문자열)
  changeReason?: string
  requestedBy?: string
  requestedByName?: string
  editedBy: string
  editedByName: string
  approvalStatus: ApprovalStatus
  changesetId?: string // 개정묶음 ID (여러 revision을 하나의 개정으로 묶음)
  changesetName?: string // 개정묶음 이름 (예: 2024-07 3차 개정)
  approvedBy?: string
  approvedByName?: string
  approvedAt?: string
  rejectReason?: string
  version?: number
  createdAt: string
}

export interface SpecInput {
  specTitle: string
  sourceCompany?: string
  docNumber?: string
  equipmentId?: string
  status?: SpecStatus
}

export interface UserMeta {
  userId: string
  userName: string
}

// 수정 요청 payload (프론트에서 편집한 결과)
export interface RevisionInput {
  changeType: RevisionChangeType
  itemId?: string
  category: string
  subCategory: string
  requirementBefore?: string
  requirementAfter?: string
  guidelineBefore?: string
  guidelineAfter?: string
  remarkBefore?: string // 비고 변경 전
  remarkAfter?: string // 비고 변경 후
  partsBefore?: string // 부품 목록 변경 전 (JSON 문자열)
  partsAfter?: string // 부품 목록 변경 후 (JSON 문자열)
  changeReason?: string
  requestedByName?: string // 수정을 요청한 사람(웹 사용자가 직접 입력)
}

export function useSpecs() {
  const [specs, setSpecs] = useState<EquipmentSpec[]>([])
  const [loading, setLoading] = useState(false)

  const fetchSpecs = useCallback(async () => {
    setLoading(true)
    try {
      const { list } = await lumi.entities.equipment_specs.list({
        sort: { updatedAt: -1 },
      })
      setSpecs((list || []) as EquipmentSpec[])
      console.log("equipment_specs fetched:", list?.length)
    } catch (error) {
      console.error("사양서 목록 조회 실패:", error)
    } finally {
      setLoading(false)
    }
  }, [])

  const createSpec = useCallback(
    async (data: SpecInput, meta: UserMeta) => {
      const now = new Date().toISOString()
      const payload = {
        specTitle: data.specTitle,
        sourceCompany: data.sourceCompany || "",
        docNumber: data.docNumber || "",
        equipmentId: data.equipmentId || "",
        currentVersion: 1,
        status: data.status || "draft",
        referencedUsers: [],
        creator: meta.userId,
        creatorName: meta.userName,
        createdAt: now,
        updatedAt: now,
      }
      const created = await lumi.entities.equipment_specs.create(payload)
      console.log("equipment_spec created:", created?._id)
      await fetchSpecs()
      return created
    },
    [fetchSpecs]
  )

  const updateSpec = useCallback(
    async (id: string, data: Partial<SpecInput>) => {
      const payload: Record<string, any> = { updatedAt: new Date().toISOString() }
      if (data.specTitle !== undefined) payload.specTitle = data.specTitle
      if (data.sourceCompany !== undefined) payload.sourceCompany = data.sourceCompany
      if (data.docNumber !== undefined) payload.docNumber = data.docNumber
      if (data.equipmentId !== undefined) payload.equipmentId = data.equipmentId
      if (data.status !== undefined) payload.status = data.status
      const updated = await lumi.entities.equipment_specs.update(id, payload)
      console.log("equipment_spec updated:", id)
      await fetchSpecs()
      return updated
    },
    [fetchSpecs]
  )

  const deleteSpec = useCallback(
    async (id: string) => {
      await lumi.entities.equipment_specs.delete(id)
      console.log("equipment_spec deleted:", id)
      await fetchSpecs()
    },
    [fetchSpecs]
  )

  // 보관(Archive) 처리 / 복원
  const archiveSpec = useCallback(
    async (id: string, archived: boolean) => {
      await lumi.entities.equipment_specs.update(id, {
        archived,
        updatedAt: new Date().toISOString(),
      })
      console.log("equipment_spec archived:", id, archived)
      await fetchSpecs()
    },
    [fetchSpecs]
  )

  return { specs, loading, fetchSpecs, createSpec, updateSpec, deleteSpec, archiveSpec }
}

// ==================== 사양 항목 + 변경(수정요청/승인/반려) ====================
export function useSpecItems() {
  const [items, setItems] = useState<SpecItem[]>([])
  const [revisions, setRevisions] = useState<SpecItemRevision[]>([])
  const [loading, setLoading] = useState(false)

  // 확정된 사양 항목 조회
  const fetchItems = useCallback(async (specId: string) => {
    if (!specId) return
    setLoading(true)
    try {
      const { list } = await lumi.entities.spec_items.list({
        filter: { specId },
        sort: { updatedAt: 1 },
      })
      // [정렬 정책] 수정/등록한 시간 순서(먼저 처리한 항목이 상단)로 정렬한다.
      // - 필수 대분류(공장입력전원, sortOrder === -1)는 항상 최상단에 고정
      // - 그 외 항목은 updatedAt 오름차순(가장 최근에 수정/등록한 항목이 하단)
      const MANDATORY_SORT = -1
      const sorted = ((list || []) as SpecItem[]).slice().sort((a, b) => {
        const aMand = (a.sortOrder ?? 0) === MANDATORY_SORT
        const bMand = (b.sortOrder ?? 0) === MANDATORY_SORT
        if (aMand !== bMand) return aMand ? -1 : 1
        const ta = new Date(a.updatedAt || a.createdAt || 0).getTime()
        const tb = new Date(b.updatedAt || b.createdAt || 0).getTime()
        return ta - tb
      })
      setItems(sorted)
      console.log("spec_items fetched:", list?.length)
    } catch (error) {
      console.error("사양 항목 조회 실패:", error)
    } finally {
      setLoading(false)
    }
  }, [])

  // 변경 이력(수정요청/승인/반려) 조회
  const fetchRevisions = useCallback(async (specId: string) => {
    if (!specId) return
    try {
      const { list } = await lumi.entities.spec_item_revisions.list({
        filter: { specId },
        sort: { createdAt: -1 },
      })
      setRevisions((list || []) as SpecItemRevision[])
      console.log("spec_item_revisions fetched:", list?.length)
    } catch (error) {
      console.error("변경 이력 조회 실패:", error)
    }
  }, [])

  // 수정 요청 생성 = 미승인(pending) revision 저장. 실제 spec_items는 아직 변경하지 않음.
  // editedBy = 실제 웹에서 수정한 직원, requestedByName = 수정을 요청한 사람
  const submitRevision = useCallback(
    async (specId: string, input: RevisionInput, editor: UserMeta) => {
      const now = new Date().toISOString()
      const payload = {
        specId,
        itemId: input.itemId || "",
        changeType: input.changeType,
        category: input.category,
        subCategory: input.subCategory,
        requirementBefore: input.requirementBefore || "",
        requirementAfter: input.requirementAfter || "",
        guidelineBefore: input.guidelineBefore || "",
        guidelineAfter: input.guidelineAfter || "",
        remarkBefore: input.remarkBefore || "",
        remarkAfter: input.remarkAfter || "",
        partsBefore: input.partsBefore || "",
        partsAfter: input.partsAfter || "",
        changeReason: input.changeReason || "",
        requestedBy: "",
        requestedByName: input.requestedByName || "",
        editedBy: editor.userId,
        editedByName: editor.userName,
        approvalStatus: "pending" as ApprovalStatus,
        createdAt: now,
      }
      const created = await lumi.entities.spec_item_revisions.create(payload)
      console.log("spec_item_revision submitted (pending):", created?._id)
      await fetchRevisions(specId)
      return created
    },
    [fetchRevisions]
  )

  // 삭제 요청 생성 = 미승인(pending) + changeType "delete" revision 저장.
  // 팀장급 승인 시 해당 사양 항목이 DB에서 완전히 삭제된다.
  const submitDeleteRevision = useCallback(
    async (specId: string, item: SpecItem, editor: UserMeta, requestedByName: string, changeReason: string) => {
      const now = new Date().toISOString()
      const payload = {
        specId,
        itemId: item._id,
        changeType: "delete" as RevisionChangeType,
        category: item.category,
        subCategory: item.subCategory,
        requirementBefore: item.requirement || "",
        requirementAfter: "",
        guidelineBefore: item.guideline || "",
        guidelineAfter: "",
        remarkBefore: item.remark || "",
        remarkAfter: "",
        partsBefore: serializeParts(item.parts),
        partsAfter: "",
        changeReason: changeReason || "",
        requestedBy: "",
        requestedByName: requestedByName || "",
        editedBy: editor.userId,
        editedByName: editor.userName,
        approvalStatus: "pending" as ApprovalStatus,
        createdAt: now,
      }
      const created = await lumi.entities.spec_item_revisions.create(payload)
      console.log("spec_item_revision delete requested (pending):", created?._id)
      await fetchRevisions(specId)
      return created
    },
    [fetchRevisions]
  )

  // 대분류 항목 즉시 추가 (승인 불필요, 모든 사용자 가능) - revision 생성 없이 즉시 반영
  // 대분류만 입력하면 중분류(subCategory)는 스키마 required 대응을 위해 기본값을 넣는다.
  const addCategoryItem = useCallback(
    async (
      spec: EquipmentSpec,
      data: { category: string; subCategory?: string; remark?: string },
      editor: UserMeta
    ) => {
      const now = new Date().toISOString()
      // 현재 항목 수 기준으로 sortOrder 이어붙이기
      let sortOrder = 999
      try {
        const { list } = await lumi.entities.spec_items.list({ filter: { specId: spec._id } })
        sortOrder = (list?.length || 0) + 1
      } catch (e) {
        console.error("sortOrder 계산 실패:", e)
      }
      const created = await lumi.entities.spec_items.create({
        specId: spec._id,
        category: data.category,
        // subCategory는 스키마 required이므로 비어있으면 안전한 기본값 사용
        subCategory: (data.subCategory || "").trim() || "기본",
        requirement: "",
        guideline: "",
        remark: data.remark || "",
        parts: [],
        actualSpec: "",
        unit: "",
        sortOrder,
        version: spec.currentVersion || 1,
        creator: editor.userId,
        createdAt: now,
        updatedAt: now,
      })
      console.log("spec_item category added (no-approval):", created?._id)
      await fetchItems(spec._id)
      return created
    },
    [fetchItems]
  )

  // 사양 항목 즉시 완전 삭제 (팀장급, 승인 절차 없음) - DB에서 완전 제거
  const deleteItem = useCallback(
    async (specId: string, itemId: string) => {
      await lumi.entities.spec_items.delete(itemId)
      console.log("spec_item deleted (hard):", itemId)
      await fetchItems(specId)
    },
    [fetchItems]
  )

  // 승인 = revision 내용을 spec_items에 실제 반영 + currentVersion +1
  const approveRevision = useCallback(
    async (
      spec: EquipmentSpec,
      revision: SpecItemRevision,
      approver: UserMeta
    ) => {
      const now = new Date().toISOString()
      const nextVersion = (spec.currentVersion || 1) + 1

      // 1) 실제 사양 항목 반영
      if (revision.changeType === "create") {
        // 기존 항목 수 기준으로 sortOrder 지정
        let sortOrder = 999
        try {
          const { list } = await lumi.entities.spec_items.list({ filter: { specId: spec._id } })
          sortOrder = (list?.length || 0) + 1
        } catch (e) {
          console.error("sortOrder 계산 실패:", e)
        }
        await lumi.entities.spec_items.create({
          specId: spec._id,
          category: revision.category,
          subCategory: revision.subCategory,
          requirement: revision.requirementAfter || "",
          guideline: revision.guidelineAfter || "",
          remark: revision.remarkAfter || "",
          parts: parseParts(revision.partsAfter),
          unit: "",
          sortOrder,
          version: nextVersion,
          creator: revision.editedBy,
          createdAt: now,
          updatedAt: now,
        })
      } else if (revision.changeType === "update" && revision.itemId) {
        await lumi.entities.spec_items.update(revision.itemId, {
          category: revision.category,
          subCategory: revision.subCategory,
          requirement: revision.requirementAfter || "",
          guideline: revision.guidelineAfter || "",
          remark: revision.remarkAfter || "",
          parts: parseParts(revision.partsAfter),
          version: nextVersion,
          updatedAt: now,
        })
      } else if (revision.changeType === "delete" && revision.itemId) {
        await lumi.entities.spec_items.delete(revision.itemId)
      }

      // 2) revision 처리: 삭제 승인은 이력을 남기지 않고 DB에서 완전 제거, 그 외는 승인 상태로 갱신
      if (revision.changeType === "delete") {
        await lumi.entities.spec_item_revisions.delete(revision._id)
        console.log("spec_item_revision (delete) approved & hard-deleted:", revision._id)
      } else {
        await lumi.entities.spec_item_revisions.update(revision._id, {
          approvalStatus: "approved",
          approvedBy: approver.userId,
          approvedByName: approver.userName,
          approvedAt: now,
          version: nextVersion,
        })
      }

      // 3) 사양서 버전 증가
      await lumi.entities.equipment_specs.update(spec._id, {
        currentVersion: nextVersion,
        updatedAt: now,
      })

      console.log("spec_item_revision approved:", revision._id, "-> v", nextVersion)
      await Promise.all([fetchItems(spec._id), fetchRevisions(spec._id)])
    },
    [fetchItems, fetchRevisions]
  )

  // ===== 방안 B: 개정묶음(Changeset) 일괄 승인 =====
  // 여러 pending revision을 하나의 개정으로 묶어 한 번에 반영. 버전은 +1만 증가.
  const approveChangeset = useCallback(
    async (
      spec: EquipmentSpec,
      revisionList: SpecItemRevision[],
      approver: UserMeta,
      changesetName: string
    ) => {
      if (!revisionList || revisionList.length === 0) return
      const now = new Date().toISOString()
      const nextVersion = (spec.currentVersion || 1) + 1
      const changesetId = `cs_${Date.now()}_${Math.random().toString(36).slice(2, 8)}`

      // 신규 생성 항목의 sortOrder 기준값 (현재 항목 수)
      let baseSort = 0
      try {
        const { list } = await lumi.entities.spec_items.list({ filter: { specId: spec._id } })
        baseSort = list?.length || 0
      } catch (e) {
        console.error("sortOrder 기준 계산 실패:", e)
      }

      // 1) 각 revision 내용을 spec_items에 순차 반영 (동일 버전 번호 공유)
      let createSeq = 1
      for (const revision of revisionList) {
        if (revision.changeType === "create") {
          await lumi.entities.spec_items.create({
            specId: spec._id,
            category: revision.category,
            subCategory: revision.subCategory,
            requirement: revision.requirementAfter || "",
            guideline: revision.guidelineAfter || "",
            remark: revision.remarkAfter || "",
            parts: parseParts(revision.partsAfter),
            unit: "",
            sortOrder: baseSort + createSeq,
            version: nextVersion,
            creator: revision.editedBy,
            createdAt: now,
            updatedAt: now,
          })
          createSeq += 1
        } else if (revision.changeType === "update" && revision.itemId) {
          await lumi.entities.spec_items.update(revision.itemId, {
            category: revision.category,
            subCategory: revision.subCategory,
            requirement: revision.requirementAfter || "",
            guideline: revision.guidelineAfter || "",
            remark: revision.remarkAfter || "",
            parts: parseParts(revision.partsAfter),
            version: nextVersion,
            updatedAt: now,
          })
        } else if (revision.changeType === "delete" && revision.itemId) {
          await lumi.entities.spec_items.delete(revision.itemId)
        }

        // 2) revision 처리: 삭제 승인은 이력을 남기지 않고 DB에서 완전 제거, 그 외는 승인 + changeset 정보 기록
        if (revision.changeType === "delete") {
          await lumi.entities.spec_item_revisions.delete(revision._id)
          console.log("spec_item_revision (delete) approved & hard-deleted:", revision._id)
        } else {
          await lumi.entities.spec_item_revisions.update(revision._id, {
            approvalStatus: "approved",
            approvedBy: approver.userId,
            approvedByName: approver.userName,
            approvedAt: now,
            version: nextVersion,
            changesetId,
            changesetName: changesetName || "",
          })
        }
      }

      // 3) 사양서 버전 +1 + 마지막 승인일(=마지막 수정일) + 개정안 이름 + 누적 수정 건수 갱신
      // [정책] 개정안 이름이 "최초 작성"인 경우 총 수정 반영 횟수(totalRevisionCount)는 증가시키지 않는다.
      //        최초 작성은 "수정"이 아니므로 여러 번 최초 작성으로 승인해도 수정 반영 횟수는 계속 0으로 유지된다.
      const isInitialWrite = (changesetName || "").trim() === INITIAL_WRITE_NAME
      const nextTotal = isInitialWrite
        ? spec.totalRevisionCount || 0
        : (spec.totalRevisionCount || 0) + revisionList.length
      await lumi.entities.equipment_specs.update(spec._id, {
        currentVersion: nextVersion,
        lastApprovedAt: now,
        lastApprovedVersion: nextVersion,
        lastChangesetName: changesetName || "",
        totalRevisionCount: nextTotal,
        updatedAt: now,
      })

      console.log(
        "spec changeset approved:",
        changesetId,
        "revisions:",
        revisionList.length,
        "-> v",
        nextVersion,
        "total:",
        nextTotal
      )
      await Promise.all([fetchItems(spec._id), fetchRevisions(spec._id)])
      return {
        changesetId,
        version: nextVersion,
        approvedAt: now,
        changesetName: changesetName || "",
        totalRevisionCount: nextTotal,
      }
    },
    [fetchItems, fetchRevisions]
  )

  // 필수 대분류(공장입력전원) 보장: 해당 사양서에 항목이 없으면 기본 항목 1개를 즉시 생성
  const ensureMandatoryCategory = useCallback(
    async (spec: EquipmentSpec, editor: UserMeta) => {
      if (!spec?._id) return
      const MANDATORY_CATEGORY = "공장입력전원"
      try {
        const { list } = await lumi.entities.spec_items.list({ filter: { specId: spec._id } })
        const existed = (list || []) as SpecItem[]
        const hasMandatory = existed.some((it) => it.category === MANDATORY_CATEGORY)
        if (hasMandatory) return
        const now = new Date().toISOString()
        await lumi.entities.spec_items.create({
          specId: spec._id,
          category: MANDATORY_CATEGORY,
          subCategory: "입력 전원",
          requirement: "",
          guideline: "",
          parts: [],
          actualSpec: "",
          unit: "",
          remark: "필수 항목(자동 생성)",
          sortOrder: -1, // 항상 최상단에 위치
          version: spec.currentVersion || 1,
          creator: editor.userId,
          createdAt: now,
          updatedAt: now,
        })
        console.log("mandatory category ensured:", MANDATORY_CATEGORY, spec._id)
        await fetchItems(spec._id)
      } catch (e) {
        console.error("필수 대분류 생성 실패:", e)
      }
    },
    [fetchItems]
  )

  // 실제 제작 사양 직접 수정 (승인 불필요, 모든 사용자 가능) - revision 생성 없이 즉시 반영
  const updateActualSpec = useCallback(
    async (specId: string, itemId: string, actualSpec: string) => {
      await lumi.entities.spec_items.update(itemId, {
        actualSpec: actualSpec || "",
        updatedAt: new Date().toISOString(),
      })
      console.log("spec_item actualSpec updated:", itemId)
      await fetchItems(specId)
    },
    [fetchItems]
  )

  // 반려 = 사양서 원본 유지 + 반려된 revision을 DB에서 완전 삭제 (DB 용량 관리)
  const rejectRevision = useCallback(
    async (specId: string, revisionId: string) => {
      await lumi.entities.spec_item_revisions.delete(revisionId)
      console.log("spec_item_revision rejected & hard-deleted:", revisionId)
      await fetchRevisions(specId)
    },
    [fetchRevisions]
  )

  // 개정묶음 일괄 반려 = 선택된 여러 pending revision을 한 번에 DB에서 완전 삭제 (DB 용량 관리)
  const rejectChangeset = useCallback(
    async (specId: string, revisionList: SpecItemRevision[]) => {
      if (!revisionList || revisionList.length === 0) return 0
      for (const rev of revisionList) {
        await lumi.entities.spec_item_revisions.delete(rev._id)
      }
      console.log("spec_item_revisions rejected & hard-deleted (bulk):", revisionList.length)
      await fetchRevisions(specId)
      return revisionList.length
    },
    [fetchRevisions]
  )

  // 템플릿 불러오기: 항목 목록을 현재 사양서에 즉시 생성 (승인 불필요)
  const importItems = useCallback(
    async (
      specId: string,
      templateItems: Partial<SpecItem>[],
      editor: UserMeta
    ) => {
      if (!specId || !templateItems || templateItems.length === 0) return 0
      const now = new Date().toISOString()
      // 현재 항목 수 기준으로 sortOrder 이어붙이기
      let baseSort = 0
      try {
        const { list } = await lumi.entities.spec_items.list({ filter: { specId } })
        baseSort = list?.length || 0
      } catch (e) {
        console.error("sortOrder 기준 계산 실패:", e)
      }
      let seq = 1
      for (const it of templateItems) {
        await lumi.entities.spec_items.create({
          specId,
          category: it.category || "",
          subCategory: it.subCategory || "",
          requirement: it.requirement || "",
          guideline: it.guideline || "",
          remark: it.remark || "",
          parts: Array.isArray(it.parts) ? it.parts : [],
          actualSpec: "",
          unit: it.unit || "",
          sortOrder: baseSort + seq,
          version: 1,
          creator: editor.userId,
          createdAt: now,
          updatedAt: now,
        })
        seq += 1
      }
      console.log("template items imported:", templateItems.length, "-> spec:", specId)
      await fetchItems(specId)
      return templateItems.length
    },
    [fetchItems]
  )

  return {
    items,
    revisions,
    loading,
    fetchItems,
    fetchRevisions,
    submitRevision,
    submitDeleteRevision,
    addCategoryItem,
    deleteItem,
    approveRevision,
    approveChangeset,
    rejectRevision,
    rejectChangeset,
    updateActualSpec,
    ensureMandatoryCategory,
    importItems,
  }
}

// ==================== 사양 템플릿 (저장/불러오기) ====================
export interface SpecTemplate {
  _id: string
  templateName: string
  description?: string
  items: string // JSON 직렬화된 SpecItem 부분 배열
  itemCount?: number
  creator: string
  creatorName?: string
  createdAt: string
  updatedAt: string
}

// 템플릿에 저장할 항목의 최소 필드
export interface TemplateItem {
  category: string
  subCategory: string
  requirement?: string
  guideline?: string
  remark?: string
  unit?: string
  parts?: SpecPart[]
}

export function useSpecTemplates() {
  const [templates, setTemplates] = useState<SpecTemplate[]>([])
  const [loading, setLoading] = useState(false)

  const fetchTemplates = useCallback(async () => {
    setLoading(true)
    try {
      const { list } = await lumi.entities.spec_templates.list({
        sort: { updatedAt: -1 },
      })
      setTemplates((list || []) as SpecTemplate[])
      console.log("spec_templates fetched:", list?.length)
    } catch (error) {
      console.error("템플릿 목록 조회 실패:", error)
    } finally {
      setLoading(false)
    }
  }, [])

  // 현재 사양 항목들을 템플릿으로 저장
  const saveTemplate = useCallback(
    async (
      templateName: string,
      items: TemplateItem[],
      meta: UserMeta,
      description?: string
    ) => {
      const now = new Date().toISOString()
      const created = await lumi.entities.spec_templates.create({
        templateName,
        description: description || "",
        items: JSON.stringify(items || []),
        itemCount: items?.length || 0,
        creator: meta.userId,
        creatorName: meta.userName,
        createdAt: now,
        updatedAt: now,
      })
      console.log("spec_template saved:", created?._id)
      await fetchTemplates()
      return created
    },
    [fetchTemplates]
  )

  const deleteTemplate = useCallback(
    async (id: string) => {
      await lumi.entities.spec_templates.delete(id)
      console.log("spec_template deleted:", id)
      await fetchTemplates()
    },
    [fetchTemplates]
  )

  // 템플릿 items JSON을 파싱하여 항목 배열 반환
  const parseTemplateItems = useCallback((tpl: SpecTemplate): TemplateItem[] => {
    try {
      const arr = JSON.parse(tpl.items || "[]")
      return Array.isArray(arr) ? (arr as TemplateItem[]) : []
    } catch {
      return []
    }
  }, [])

  return { templates, loading, fetchTemplates, saveTemplate, deleteTemplate, parseTemplateItems }
}
